/**
 * AR Vision Demo — demo-mode flag (delegates to the shared global toggle).
 *
 * THROWAWAY DEMO CODE. Everything under `frontend/src/demo/ar/` exists only to
 * drive the UI-only "AR Vision Demo" (Linear GEN-2247) and is meant to be
 * deleted wholesale afterwards. It must never be imported by production AR code
 * except via the thin guards in the AR query-hook shims.
 *
 * AR no longer owns its own flag — it shares the single global demo switch in
 * `@/demo/core/demo-mode`. These wrappers keep AR's familiar `isArDemoMode()` /
 * `setArDemoMode()` API for its many hook-shim call sites. Demo on/off is
 * toggled solely by the Cmd+K demo command.
 */

import {
  isDemoMode,
  setDemoMode,
  refreshDemoMode,
} from '@/demo/core/demo-mode';

/** True when demo mode is on. Safe to call anywhere. */
export function isArDemoMode(): boolean {
  return isDemoMode();
}

/** Re-read the flag from sessionStorage (e.g. after navigation). */
export function refreshArDemoMode(): boolean {
  return refreshDemoMode();
}

/**
 * True when the user has asked the OS for reduced motion. Demo animations
 * (e.g. the portal step-through, F3) collapse their timings when this is set.
 * Safe to call anywhere; defaults to `false` outside a browser.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Toggle demo mode (used by the reset/exit control + force-enable bootstrap). */
export function setArDemoMode(enabled: boolean): void {
  setDemoMode(enabled);
}
